import {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {SelectPicker} from 'rsuite';
import Swal from 'sweetalert2';
import PageHeader from '../Pages/Fertility/PageHeader';
import {fetchPatientDetailsUuid} from '../Redux/Thunk/Actions';
import {createFertility, getAllFertility, updateFertility} from '../Redux/Thunk/fertiltyActions';
import '../styles/Components/Fertility.scss';
import {convertToLocal} from '../utils/dateTime';
import FormInputWithLabel from './Global_Components/form/FormInputWithLabel';

function AddFertility({loaderchange, popupalert, popuptext}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const sideactive = useSelector(state => state.counter.sidebarnav);
  const editMode = useSelector(state => state.fertility.editDetails);
  const fertilityData = useSelector(state => state.fertility.fertilityData);
  console.log(`fertilityData -->`, fertilityData);

  const [treatmentType] = useState({
    IUI: 'IUI',
    IVF: 'IVF Embryo Transfer',
    OI: 'Ovulation Induction'
  });
  const [tubalpatency] = useState({
    bso: 'both side open',
    osb: 'one side blocked',
    bsb: 'both side blocked'
  });
  const [result] = useState({
    Positive: 'Positive',
    Negative: 'Negative'
  });

  const treatmentOptions = Object.entries(treatmentType).map(([key, value]) => ({
    label: value,
    value: key
  }));
  const tubalpatencyOptions = Object.entries(tubalpatency).map(([key, value]) => ({
    label: value,
    value: key
  }));
  const resultOptions = Object.entries(result).map(([key, value]) => ({
    label: value,
    value: key
  }));

  const [formData, setFormData] = useState(() =>
    editMode
      ? {
          ...fertilityData,
          oi: fertilityData.oi || {number_of_follicles_ruptured: '', endometrial_thickness: ''},
          iui: fertilityData.iui || {
            follicle_status: '',
            endometrial_thickness: '',
            number_of_follicles: '',
            tmsc: '',
            type_of_iui: '',
            sitting: ''
          },
          ivf: fertilityData.ivf || {
            embryos_transferred: '',
            transfer_type: '',
            embryo_glue: '',
            era: '',
            cervical_shaving: '',
            anaesthesia: '',
            embryo_grading: '',
            pgta_result: '',
            endometrial_thickness: ''
          }
        }
      : {
          patient_id: '',
          treatment_type: '',
          cycle: '',
          start_date: '',
          procedure_date: '',
          trigger_date: '',
          beta_hcg_test_date: '',
          result: '',
          remarks: '',
          fsh: '',
          lh: '',
          e2: '',
          amh: '',
          tsh_prolactin: '',
          progesterone: '',
          sperm_factors: '',
          tubal_patency: '',

          // nested treatment-specific objects
          oi: {
            number_of_follicles_ruptured: '',
            endometrial_thickness: ''
          },
          iui: {
            follicle_status: '',
            endometrial_thickness: '',
            number_of_follicles: '',
            tmsc: '',
            type_of_iui: '',
            sitting: ''
          },
          ivf: {
            embryos_transferred: '',
            transfer_type: '',
            embryo_glue: '',
            era: '',
            cervical_shaving: '',
            anaesthesia: '',
            embryo_grading: '',
            pgta_result: '',
            endometrial_thickness: ''
          }
        }
  );

  const [validationErrors, setValidationErrors] = useState({});

  const baseRequiredFields = [
    'patient_p_uhid',
    'patient_name',
    'treatment_type',
    'cycle',
    'fsh',
    'lh',
    'e2',
    'amh',
    'tsh_prolactin',
    'progesterone',
    'sperm_factors',
    'tubal_patency',
    'start_date',
    'procedure_date',
    'trigger_date',
    'beta_hcg_test_date'
  ];

  const treatmentFieldMap = {
    IUI: ['follicle_status', 'endometrial_thickness', 'number_of_follicles', 'tmsc', 'type_of_iui', 'sitting'],
    IVF: ['embryos_transferred', 'transfer_type', 'embryo_glue', 'era', 'cervical_shaving', 'anaesthesia', 'embryo_grading', 'pgt_a_result', 'endometrial_thickness'],
    OI: ['number_of_follicles_ruptured', 'endometrial_thickness']
  };

  const requiredFields = [...baseRequiredFields, ...(treatmentFieldMap[treatmentType] || [])];

  // const isFormValid = requiredFields.every(field => formData[field]?.toString().trim() !== '');

  const isFormValid = () => {
    // Base validation
    for (let field of baseRequiredFields) {
      if (!formData[field] || formData[field].toString().trim() === '') {
        return false;
      }
    }

    // Nested treatment-specific validation
    const treatment = formData.treatment_type;
    const nestedFields = treatmentFieldMap[treatment] || [];

    for (let field of nestedFields) {
      const nestedValue = formData?.[treatment?.toLowerCase()]?.[field];
      if (!nestedValue || nestedValue.toString().trim() === '') {
        return false;
      }
    }

    return true;
  };

  const validateForm = () => {
    const errors = {};

    // Validate base fields
    baseRequiredFields.forEach(field => {
      if (!formData[field] || formData[field].toString().trim() === '') {
        errors[field] = 'This field is required';
      }
    });

    // Validate nested treatment-specific fields
    const treatment = formData.treatment_type;
    const treatmentFields = treatmentFieldMap[treatment] || [];

    treatmentFields.forEach(field => {
      const nestedValue = formData?.[treatment?.toLowerCase()]?.[field];
      if (!nestedValue || nestedValue.toString().trim() === '') {
        // Use a unique key for nested errors
        errors[`${treatment.toLowerCase()}.${field}`] = 'This field is required';
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = e => {
    console.log(`handleInputChange -->`);
    const {name, value} = e.target;

    setFormData(prev => ({...prev, [name]: value}));

    if (name === 'patient_p_uhid' && /^(ASCAS|VDASCAS)\d+$/i.test(value)) {
      fetchPatientDetails(value);
    }
  };

  const handleNestedChange = (section, name, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [name]: value
      }
    }));
  };

  const fetchPatientDetails = async uhid => {
    loaderchange('true'); // if loaderchange accepts boolean

    try {
      const addPatient = fetchPatientDetailsUuid({p_uhid: uhid});
      const response = await dispatch(addPatient).unwrap();
      console.log(`response -->`, response);

      if (response?.status?.code === 200 && response?.data?.data?.length > 0) {
        const normalizedUhid = uhid.trim().toUpperCase();
        const patientData = response.data.data.find(patient => patient.p_uhid?.toUpperCase() === normalizedUhid);

        if (!patientData) {
          setFormData(prev => ({
            ...prev,
            patient_name: '',
            patient_age: '',
            patient_id: ''
          }));
          Swal.fire({
            title: 'Error.',
            text: 'No Patient Found.',
            icon: 'warning',
            showConfirmButton: false,
            timer: 1500
          });
          return;
        }

        setFormData(prev => ({
          ...prev,
          patient_name: `${patientData.first_name} ${patientData.last_name}`,
          patient_age: patientData.age,
          patient_id: patientData.id
        }));
      } else {
        Swal.fire({
          title: 'Error.',
          text: 'No Patient Found.',
          icon: 'warning',
          showConfirmButton: false,
          timer: 1500
        });
      }
    } catch (error) {
      console.error('Error fetching patient details:', error);
      Swal.fire({
        title: 'Error.',
        text: 'Failed to get patient details.',
        icon: 'error',
        showConfirmButton: false,
        timer: 1500
      });
    } finally {
      loaderchange('false');
    }
  };

  const handleSubmit = async e => {
    console.log(`handleSubmit -->`);
    e.preventDefault();
    if (!validateForm()) return;
    loaderchange('true');
    const cleanedFormData = {
      ...formData,
      oi: formData.treatment_type === 'OI' ? formData.oi : null,
      iui: formData.treatment_type === 'IUI' ? formData.iui : null,
      ivf: formData.treatment_type === 'IVF' ? formData.ivf : null
    };
    try {
      if (editMode) {
        try {
          const response = await dispatch(updateFertility(cleanedFormData)).unwrap();
          console.log(`response -->`, response);
          if (response?.status?.code === 200) {
            Swal.fire({
              title: 'Successful.',
              text: 'Fertility updated successfully.',
              icon: 'success',
              showConfirmButton: false,
              timer: 1500
            });
            dispatch(getAllFertility());
          } else {
            Swal.fire({
              title: 'Error.',
              text: `${response?.status?.message ? response?.status?.message : 'Failed to Update Fertility.'}`,
              icon: 'warning',
              showConfirmButton: false,
              timer: 1500
            });
          }
        } catch (error) {
          console.log(`error -->`, error);
          Swal.fire({
            title: 'Error.',
            text: 'Failed to Update Fertility.',
            icon: 'warning',
            showConfirmButton: false,
            timer: 1500
          });
        }
      } else {
        try {
          const response = await dispatch(createFertility(cleanedFormData)).unwrap();

          if (response?.status?.code === 201) {
            Swal.fire({
              title: 'Successful.',
              text: 'Fertility created successful.',
              icon: 'success',
              showConfirmButton: false,
              timer: 1500
            });
            dispatch(getAllFertility());
          }
        } catch (error) {
          Swal.fire({
            title: 'Error.',
            text: error.message || 'Failed to create Fertility.',
            icon: 'warning',
            showConfirmButton: false,
            timer: 1500
          });
        }
      }
    } catch (error) {
      console.error('Error saving Fertility:', error);
    } finally {
      loaderchange('false');
      const from = location.state?.from;
      console.log(`from -->`, from);
      if (from === '/fertility' || from === '/fertility/view') {
        navigate(from, {replace: true});
      }
    }
  };

  const handleTreatmentTypeChange = value => {
    setFormData(prev => {
      const updated = {
        ...prev,
        treatment_type: value
      };

      if (value === 'OI' && !prev.oi) {
        updated.oi = {number_of_follicles_ruptured: '', endometrial_thickness: ''};
      } else if (value === 'IUI' && !prev.iui) {
        updated.iui = {
          follicle_status: '',
          endometrial_thickness: '',
          number_of_follicles: '',
          tmsc: '',
          type_of_iui: '',
          sitting: ''
        };
      } else if (value === 'IVF' && !prev.ivf) {
        updated.ivf = {
          embryos_transferred: '',
          transfer_type: '',
          embryo_glue: '',
          era: '',
          cervical_shaving: '',
          anaesthesia: '',
          embryo_grading: '',
          pgta_result: '',
          endometrial_thickness: ''
        };
      }

      return updated;
    });
  };

  return (
    <div className={sideactive ? 'maincontainer-patient-sa' : 'maincontainer-patient'}>
      <div className='patient-header'>
        {/* <PageHeader title='Create Fertility' type='add' /> */}
        <PageHeader title={editMode ? 'Edit Fertility' : 'Create Fertility'} type={editMode ? 'edit' : 'add'} />

        <div className='row'>
          <div className='col-md-4'>
            <FormInputWithLabel
              label='UHID'
              name='patient_p_uhid'
              value={formData.patient_p_uhid}
              required
              onChange={handleInputChange}
              inputClassName='show'
              error={validationErrors.patient_p_uhid}
            />
          </div>
          <div className='col-md-4'>
            <FormInputWithLabel label='NAME' name='patient_name' value={formData.patient_name} required onChange={handleInputChange} inputClassName='show' error={validationErrors.patient_name} />
          </div>
          <div className='col-md-4'>
            <FormInputWithLabel label='AGE' name='patient_age' value={formData.patient_age} required={false} onChange={handleInputChange} inputClassName='show' />
          </div>
        </div>

        <div className='row my-4'>
          <div className='col-md-4'>
            <div className='d-flex'>
              <label className='fw-semibold mb-0 form-label align-self-start' style={{width: '120px', display: 'inline-block', verticalAlign: 'middle'}}>
                Treatment Type
                <span className='text-danger'>*</span>
              </label>

              <span className='fw-semibold ps-4 pe-2 pt-2' style={{verticalAlign: 'middle'}}>
                :
              </span>

              <div className='flex-grow-1' style={{position: 'relative'}}>
                <SelectPicker
                  data={treatmentOptions}
                  style={{width: 230, padding: '0 10px'}}
                  searchable={false}
                  placeholder='Treatment Type'
                  value={formData.treatment_type}
                  onChange={handleTreatmentTypeChange}
                />

                {/* Error message positioned absolutely */}
                {validationErrors.treatment_type && (
                  <small className='text-danger d-block mt-1 mx-3' style={{position: 'absolute', bottom: '-20px', left: '0', width: '100%'}}>
                    {validationErrors.treatment_type}
                  </small>
                )}
              </div>
            </div>
          </div>

          <div className='col-md-4'>
            <FormInputWithLabel label='Cycle Number' name='cycle' value={formData.cycle} required onChange={handleInputChange} inputClassName='show' error={validationErrors.cycle} />
          </div>

          <div className='col-md-4'>
            <FormInputWithLabel label='FSH' name='fsh' value={formData.fsh} required onChange={handleInputChange} inputClassName='show' error={validationErrors.fsh} />
          </div>
        </div>

        <div className='row my-4'>
          <div className='col-md-4'>
            <FormInputWithLabel label='LH' name='lh' value={formData.lh} required onChange={handleInputChange} inputClassName='show' error={validationErrors.lh} />
          </div>
          <div className='col-md-4'>
            <FormInputWithLabel label='E2' name='e2' value={formData.e2} required onChange={handleInputChange} inputClassName='show' error={validationErrors.e2} />
          </div>
          <div className='col-md-4'>
            <FormInputWithLabel label='AMH' name='amh' value={formData.amh} required onChange={handleInputChange} inputClassName='show' error={validationErrors.amh} />
          </div>
        </div>

        <div className='row my-4'>
          <div className='col-md-4'>
            <FormInputWithLabel
              label='TSH - Prolactin'
              name='tsh_prolactin'
              value={formData.tsh_prolactin}
              required
              onChange={handleInputChange}
              inputClassName='show'
              error={validationErrors.tsh_prolactin}
            />
          </div>
          <div className='col-md-4'>
            <FormInputWithLabel
              label='Progresterone'
              name='progesterone'
              value={formData.progesterone}
              required
              onChange={handleInputChange}
              inputClassName='show'
              error={validationErrors.progesterone}
            />
          </div>
          <div className='col-md-4'>
            <FormInputWithLabel
              label='Sperm factors'
              name='sperm_factors'
              value={formData.sperm_factors}
              required
              onChange={handleInputChange}
              inputClassName='show'
              error={validationErrors.sperm_factors}
            />
          </div>
        </div>
        <div className='row my-4'>
          <div className='col-md-4'>
            <div className='d-flex'>
              <label className='fw-semibold mb-0 form-label align-self-start' style={{width: '120px', display: 'inline-block', verticalAlign: 'middle'}}>
                Tubal patency
                <span className='text-danger'>*</span>
              </label>

              <span className='fw-semibold ps-4 pe-2 pt-2' style={{verticalAlign: 'middle'}}>
                :
              </span>

              <div className='flex-grow-1' style={{position: 'relative'}}>
                <SelectPicker
                  data={tubalpatencyOptions}
                  style={{width: 230, padding: '0 10px'}}
                  searchable={false}
                  placeholder='Tubal patency'
                  value={formData.tubal_patency}
                  onChange={val => setFormData({...formData, tubal_patency: val})}
                />

                {/* Error message positioned absolutely */}
                {validationErrors.tubal_patency && (
                  <small className='text-danger d-block mt-1 mx-3' style={{position: 'absolute', bottom: '-20px', left: '0', width: '100%'}}>
                    {validationErrors.tubal_patency}
                  </small>
                )}
              </div>
            </div>
          </div>

          <div className='col-md-4'>
            <FormInputWithLabel
              type='datetime-local'
              label='Start Date'
              name='startDate'
              value={formData.start_date ? convertToLocal(formData.start_date) : ''}
              required
              onChange={e => setFormData({...formData, start_date: e.target.value})}
              inputClassName='show'
              error={validationErrors.start_date}
            />
          </div>
          <div className='col-md-4'>
            <FormInputWithLabel
              type='datetime-local'
              label='Trigger Date'
              name='trigger_date'
              value={formData.trigger_date ? convertToLocal(formData.trigger_date) : ''}
              required
              onChange={e => setFormData({...formData, trigger_date: e.target.value})}
              inputClassName='show'
              error={validationErrors.trigger_date}
            />
          </div>
        </div>
        <div className='row my-4'>
          <div className='col-md-4'>
            <FormInputWithLabel
              type='datetime-local'
              label='Procedure Date'
              name='procedure_date'
              value={formData.procedure_date ? convertToLocal(formData.procedure_date) : ''}
              required
              onChange={e => setFormData({...formData, procedure_date: e.target.value})}
              inputClassName='show'
              error={validationErrors.procedure_date}
            />
          </div>
          <div className='col-md-4'>
            <FormInputWithLabel
              type='datetime-local'
              label='Beta-hCG Test Date'
              name='beta_hcg_test_date'
              value={formData.beta_hcg_test_date ? convertToLocal(formData.beta_hcg_test_date) : ''}
              required
              onChange={e => setFormData({...formData, beta_hcg_test_date: e.target.value})}
              inputClassName='show'
              error={validationErrors.beta_hcg_test_date}
            />
          </div>
        </div>

        {formData.treatment_type === 'IUI' && <IUI formData={formData} onNestedChange={handleNestedChange} validationErrors={validationErrors} />}
        {formData.treatment_type === 'IVF' && <IVF formData={formData} onNestedChange={handleNestedChange} validationErrors={validationErrors} />}
        {formData.treatment_type === 'OI' && <OI formData={formData} onNestedChange={handleNestedChange} validationErrors={validationErrors} />}
        <div className='row my-4'>
          <div className='col-md-4'>
            <label className={`fw-semibold mb-0 form-label`}>
              {'Result'}
              {/* <span className='text-danger'>*</span> */}
            </label>
            <span className='fw-semibold ps-4 pe-2'>:</span>

            <SelectPicker
              data={resultOptions}
              style={{width: 230, padding: '0 10px'}}
              searchable={false}
              placeholder='Result'
              value={formData.result}
              onChange={val => setFormData({...formData, result: val})}
            />
          </div>

          <div className='col d-flex'>
            <label className={`fw-semibold mb-0 form-label`}>
              {'Remarks'}
              {/* <span className='text-danger'>*</span> */}
            </label>
            <span className='fw-semibold ps-4 pe-2'>:</span>

            <textarea
              id='remarks'
              name='remarks'
              style={{height: '50px', width: '100%', padding: '0 10px'}}
              required={false}
              className='form-control show'
              value={formData.remarks}
              onChange={e => setFormData({...formData, remarks: e.target.value})}
            />
          </div>
        </div>
      </div>
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
        <button onClick={handleSubmit} disabled={!isFormValid()} className={`Button_Create createTime show ${!isFormValid() ? 'disabled-button' : ''}`}>
          {editMode ? 'Update' : 'Save'}
        </button>
      </div>
    </div>
  );
}

const IUI = ({formData, onNestedChange, validationErrors}) => {
  return (
    <>
      <div className='row my-4'>
        <div className='col-md-4'>
          <FormInputWithLabel
            label='Follicle Status'
            name='follicle_status'
            value={formData.iui.follicle_status}
            required
            onChange={e => onNestedChange('iui', 'follicle_status', e.target.value)}
            inputClassName='show'
            error={validationErrors['iui.follicle_status']}
          />
        </div>
        <div className='col-md-4'>
          <FormInputWithLabel
            label='Endometrial Thickness'
            name='endometrial_thickness'
            value={formData.iui.endometrial_thickness}
            required
            onChange={e => onNestedChange('iui', 'endometrial_thickness', e.target.value)}
            inputClassName='show'
            error={validationErrors['iui.endometrial_thickness']}
          />
        </div>
        <div className='col-md-4'>
          <FormInputWithLabel
            label='Number of Follicles'
            name='number_of_follicles'
            value={formData.iui.number_of_follicles}
            required
            onChange={e => onNestedChange('iui', 'number_of_follicles', e.target.value)}
            inputClassName='show'
            error={validationErrors['iui.number_of_follicles']}
          />
        </div>
      </div>
      <div className='row my-4'>
        <div className='col-md-4'>
          <FormInputWithLabel label='TMSC' name='tmsc' value={formData.iui.tmsc} required onChange={e => onNestedChange('iui', 'tmsc', e.target.value)} inputClassName='show' />
        </div>
        <div className='col-md-4'>
          <FormInputWithLabel
            label='Type of IUI'
            name='type_of_iui'
            value={formData.iui.type_of_iui}
            required
            onChange={e => onNestedChange('iui', 'type_of_iui', e.target.value)}
            inputClassName='show'
            error={validationErrors['iui.type_of_iui']}
          />
        </div>
        <div className='col-md-4'>
          <FormInputWithLabel
            label='Sitting'
            name='sitting'
            value={formData.iui.sitting}
            required
            onChange={e => onNestedChange('iui', 'sitting', e.target.value)}
            inputClassName='show'
            error={validationErrors['iui.sitting']}
          />
        </div>
      </div>
    </>
  );
};

const IVF = ({formData, onNestedChange, validationErrors}) => {
  return (
    <>
      <div className='row my-4'>
        <div className='col-md-4'>
          <FormInputWithLabel
            label='No. of Embryos Transferred'
            name='embryos_transferred'
            value={formData.ivf.embryos_transferred}
            required
            onChange={e => onNestedChange('ivf', 'embryos_transferred', e.target.value)}
            inputClassName='show'
            error={validationErrors['ivf.embryos_transferred']}
          />
        </div>
        <div className='col-md-4'>
          <FormInputWithLabel
            label='Embryo Transfer Type'
            name='transfer_type'
            value={formData.ivf.transfer_type}
            required
            onChange={e => onNestedChange('ivf', 'transfer_type', e.target.value)}
            inputClassName='show'
            error={validationErrors['ivf.transfer_type']}
          />
        </div>
        <div className='col-md-4'>
          <FormInputWithLabel
            label='Embryo Glue'
            name='embryo_glue'
            value={formData.ivf.embryo_glue}
            required
            onChange={e => onNestedChange('ivf', 'embryo_glue', e.target.value)}
            inputClassName='show'
            error={validationErrors['ivf.embryo_glue']}
          />
        </div>
      </div>
      <div className='row my-4'>
        <div className='col-md-4'>
          <FormInputWithLabel label='ERA' name='era' value={formData.ivf.era} required onChange={e => onNestedChange('ivf', 'era', e.target.value)} inputClassName='show' />
        </div>
        <div className='col-md-4'>
          <FormInputWithLabel
            label='Cervical Shaving'
            name='cervical_shaving'
            value={formData.ivf.cervical_shaving}
            required
            onChange={e => onNestedChange('ivf', 'cervical_shaving', e.target.value)}
            inputClassName='show'
            error={validationErrors['ivf.cervical_shaving']}
          />
        </div>
        <div className='col-md-4'>
          <FormInputWithLabel
            label='Anaesthesia'
            name='anaesthesia'
            value={formData.ivf.anaesthesia}
            required
            onChange={e => onNestedChange('ivf', 'anaesthesia', e.target.value)}
            inputClassName='show'
            error={validationErrors['ivf.anaesthesia']}
          />
        </div>
      </div>
      <div className='row my-4'>
        <div className='col-md-4'>
          <FormInputWithLabel
            label='Embryo Grading'
            name='embryo_grading'
            value={formData.ivf.embryo_grading}
            required
            onChange={e => onNestedChange('ivf', 'embryo_grading', e.target.value)}
            inputClassName='show'
            error={validationErrors['ivf.embryo_grading']}
          />
        </div>
        <div className='col-md-4'>
          <FormInputWithLabel
            label='PGT-A Result'
            name='pgt_a_result'
            value={formData.ivf.pgt_a_result}
            required
            onChange={e => onNestedChange('ivf', 'pgt_a_result', e.target.value)}
            inputClassName='show'
            error={validationErrors['ivf.pgt_a_result']}
          />
        </div>
        <div className='col-md-4'>
          <FormInputWithLabel
            label='Endometrial Thickness'
            name='endometrial_thickness'
            value={formData.ivf.endometrial_thickness}
            required
            onChange={e => onNestedChange('ivf', 'endometrial_thickness', e.target.value)}
            inputClassName='show'
            error={validationErrors['ivf.endometrial_thickness']}
          />
        </div>
      </div>
    </>
  );
};

const OI = ({formData, onNestedChange, validationErrors}) => {
  console.log(`onNestedChange -->`, onNestedChange);
  return (
    <>
      <div className='row my-4'>
        <div className='col-md-4'>
          <FormInputWithLabel
            label='No. of Follicles Ruptured'
            name='number_of_follicles_ruptured'
            value={formData.oi.number_of_follicles_ruptured}
            required={false}
            onChange={e => onNestedChange('oi', 'number_of_follicles_ruptured', e.target.value)}
            inputClassName='show'
            error={validationErrors['oi.number_of_follicles_ruptured']}
          />
        </div>
        <div className='col-md-4'>
          <FormInputWithLabel
            label='Endometrial Thickness'
            name='endometrial_thickness'
            value={formData.oi.endometrial_thickness}
            required
            onChange={e => onNestedChange('oi', 'endometrial_thickness', e.target.value)}
            inputClassName='show'
            error={validationErrors['oi.endometrial_thickness']}
          />
        </div>
      </div>
    </>
  );
};

export default AddFertility;
